import { useEffect, useRef, useState } from "react"
import { RootState } from "../../../store"
import { connect } from "react-redux"
import {
  fetchTrendTopTracks,
  fetchStreamsTrackMonths,
  fetchArtistTrendEconomyData,
  fetchSummaryTrackRoyalties,
  fetchSummaryTrackStreams
} from "../../../actions/trendsActions"
import { AdminTrendsProps, ArtistTrendEconomy, SortParams, TrendFilter } from "../../../types"
import { KButton, KDropdown, KInput, KSelectRange, KSelectRangeDate, KSpan } from "kahuna-base-react-components"
import Loading from "../../ui/Loading"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { toast } from "react-toastify"
import {
  ADMIN_PERMISSIONS,
  allPlatformOptions,
  errorFilledToastOptions,
  primitivePlatformOptions,
  successFilledToastOptions,
  trendsIcons
} from "../../../constants"
import { capitalize, checkAdminPermission } from "../../../utility"
// @ts-ignore
import SpotifyLogo from "../../../assets/release-platform-icons/SpotifyLogo.png"
import StreamsTopTracks from "./streams-top-tracks/StreamsTopTracks"
import StreamsTrackMonths from "./streams-track-months/StreamsTrackMonths"
import RoyaltyThreeMonths from "./royalty-three-months/RoyaltyThreeMonths"
import { platformValueMap } from "./Trends"
import { lang } from "../../../constants/languages"
import SummaryTrackRoyalties from "./summary-track-royalty/SummaryTrackRoyalties"
import SummaryTrackStreams from "./summary-track-stream/SummaryTrackStreams"

enum TabOptions {
  STREAMS_TOP_TRACKS,
  STREAMS_TRACK_MONTHS,
  ROYALTY_THREE_MONTHS,
  TRACK_ROYALTY_SUMMARY,
  TRACK_STREAM_SUMMARY
}

const initialSort: SortParams = {
  field: "",
  direction: undefined
}

// THIS COMPONENT IS SPECIFIC TO ADMIN PANEL. NEVER USE IT IN ARTIST PANEL PAGES.
const AdminTrends = ({
  trendTopTracks,
  fetchTrendTopTracks,
  fetchStreamsTrackMonths,
  artistTrendEconomyData,
  fetchArtistTrendEconomyData,
  summaryTrackRoyalties,
  fetchSummaryTrackRoyalties,
  summaryTrackStreams,
  fetchSummaryTrackStreams,
  options,
  user,
  selectedUsers
}: AdminTrendsProps) => {
  const [selectedTab, setSelectedTab] = useState<TabOptions | undefined>(undefined)
  const [filter, setFilter] = useState<TrendFilter>({
    dateValues: [null, null],
    isrcList: [],
    countryList: [],
    ageGroupList: [],
    sexList: [],
    platformList: [],
    artistList: [],
    isAdmin: true,
    minRoyalty: "100",
    minStream: "100000"
  })
  const [platformOptions, setPlatformOptions] = useState<KSelectOption[]>([])
  const [selectedPlatforms, setSelectedPlatforms] = useState<KSelectOption[]>([])
  const [loading, setLoading] = useState(false)
  const [fetchOptions, setFetchOptions] = useState(true)
  const [offset, setOffset] = useState<number>(0)
  const [limit, setLimit] = useState<number>(20)
  const [selectedRoyaltyType, setSelectedRoyaltyType] = useState<KSelectOption>()
  const [selectedResults, setSelectedResults] = useState<ArtistTrendEconomy[]>([])
  const [hasNext, setHasNext] = useState<boolean>(false)
  const twoDaysAgo = new Date()
  twoDaysAgo.setDate(twoDaysAgo.getDate() - 2)
  const formattedDate = twoDaysAgo.toISOString().slice(0, 10).replace(/-/g, "") // "YYYYMMDD"
  const [date, setDate] = useState(formattedDate)

  const [sort, setSort] = useState<SortParams>(initialSort)

  const handleSort = (field: string) => {
    let sortField = sort.field
    let sortDirection: "asc" | "desc" | undefined = sort.direction

    if (sortField === field) {
      if (!sortDirection) {
        sortDirection = "desc"
      } else if (sortDirection === "desc") {
        sortDirection = "asc"
      } else {
        sortDirection = undefined
        sortField = ""
      }
    } else {
      sortField = field
      sortDirection = "desc"
    }
    setSort({ field: sortField, direction: sortDirection })
  }

  const useTimeout = (timeoutInMs: number) => {
    setTimeout(() => {
      setLoading(false)
    }, timeoutInMs)
  }

  useEffect(() => {
    if (artistTrendEconomyData.results.length === limit) {
      setHasNext(true)
    } else {
      setHasNext(false)
    }

    setSelectedResults([...selectedResults, ...artistTrendEconomyData.results])
  }, [artistTrendEconomyData.results])

  useEffect(() => {
    if (selectedTab === undefined) return

    if (selectedTab === TabOptions.STREAMS_TOP_TRACKS) {
      setLoading(true)
      fetchTrendTopTracks(date, true, filter).then(() => {
        useTimeout(200)
      })
      return
    }

    if (selectedTab === TabOptions.STREAMS_TRACK_MONTHS) {
      setLoading(true)
      fetchStreamsTrackMonths(filter, offset, limit, selectedRoyaltyType).then(() => {
        useTimeout(100)
      })
      return
    }

    if (selectedTab === TabOptions.ROYALTY_THREE_MONTHS) {
      setLoading(true)
      fetchArtistTrendEconomyData(filter, offset, limit, selectedRoyaltyType).then(() => {
        useTimeout(100)
      })
      return
    }
    setFetchOptions(false)
  }, [filter, offset, limit, selectedRoyaltyType])

  const applySummaryFilter = () => {
    if (selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY) {
      setLoading(true)
      fetchSummaryTrackRoyalties(filter, sort).then(() => {
        useTimeout(200)
      })
    }

    if (selectedTab === TabOptions.TRACK_STREAM_SUMMARY) {
      setLoading(true)
      fetchSummaryTrackStreams(filter, sort).then(() => {
        useTimeout(200)
      })
    }
  }

  const processPlatformOptions = (platformOptions: string[]) => {
    return platformOptions.map((platform: string, index: number) => {
      return {
        label: capitalize(platform),
        value: platformValueMap?.[platform as keyof typeof platformValueMap] || index + 20,
        value2: platform,
        icon: trendsIcons[platform as keyof typeof platformValueMap],
        iconLabel: platform === "spotify" && SpotifyLogo
      }
    })
  }

  useEffect(() => {
    const userIds = selectedUsers.map((selectedUser) => selectedUser.value)
    setTimeout(() => {
      setSelectedResults([])
      setOffset(0)
      setFetchOptions(true)
      setFilter((prevFilter) => ({ ...prevFilter, selectedUsers: userIds }))
    }, 100)
  }, [selectedUsers])

  useEffect(() => {
    if (!options) {
      return
    }

    const platformOptions = options.platformOptions || []

    const platformOptionsProcessed = processPlatformOptions(platformOptions)

    if (filter.platformList.length === 0) {
      // selectedPlatforms used to show all platforms selected when no filter.platformList applied,
      // it is canceled in new logic. selectedPlatforms state might be redundant, but not deleted for now.
      //setSelectedPlatforms(platformOptionsProcessed)
      setSelectedPlatforms([])
    }

    setPlatformOptions(platformOptionsProcessed)
  }, [options])

  const findSelectedValue = (_key: string, options: KSelectOption[]) => {
    return options.filter((option) => filter[_key].includes(option.value2))
  }

  const multipleDropdownComponent = (
    title: string,
    options: KSelectOption[],
    _key: string,
    showOnlyIconsInMulti: boolean,
    width: number,
    sortSelectedFirst: boolean,
    reFetchOptions: boolean,
    dataTestId?: string
  ) => {
    return (
      <div>
        <div className="mb-1">
          <KSpan text={title} />
        </div>
        <div data-testid={dataTestId}>
          <KDropdown
            isClearable={true}
            isMulti={true}
            showOnlyIconsInMulti={showOnlyIconsInMulti}
            width={width}
            closeMenuOnSelect={false}
            options={options}
            onSelect={() => {}}
            onBlur={(selected) => {
              if (reFetchOptions) {
                setFetchOptions(true)
              }
              const value2List = (selected as KSelectOption[])?.map((entity) => entity.value2 || "") || []
              setFilter((prevFilter) => {
                if (value2List?.toString() === prevFilter[_key]?.toString()) {
                  return prevFilter
                }
                return { ...prevFilter, [_key]: value2List }
              })
              if (_key === "platformList") {
                setSelectedPlatforms(selected as KSelectOption[])
              }
            }}
            placeholder={lang.trends.filter.all}
            selected={_key === "platformList" ? selectedPlatforms : findSelectedValue(_key, options)}
            sortSelectedFirst={sortSelectedFirst}
          />
        </div>
      </div>
    )
  }

  const resetFilters = () => {
    setFilter({
      ...filter,
      ageGroupList: [],
      isrcList: [],
      artistList: [],
      sexList: [],
      countryList: [],
      platformList: []
    })
    setSelectedPlatforms([])
  }

  const toLocaleDate = (date: Date) => {
    if (selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY) {
      return date.toLocaleDateString("en-US", { month: "short", year: "numeric" })
    } else {
      return date.toLocaleDateString("en-US", { month: "short", year: "numeric", day: "2-digit" })
    }
  }

  const renderFilters = () => {
    if (selectedTab === undefined) {
      return (
        <div className="mt-10">
          <KSpan text="Select to view" />
        </div>
      )
    }

    if (selectedTab === TabOptions.ROYALTY_THREE_MONTHS || selectedTab === TabOptions.STREAMS_TRACK_MONTHS) {
      return <div className="mb-4 flex mt-10"></div>
    }

    let filteredPlatformOption: KSelectOption[] = platformOptions
    if (selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY) filteredPlatformOption = allPlatformOptions
    else if (selectedTab === TabOptions.TRACK_STREAM_SUMMARY) filteredPlatformOption = primitivePlatformOptions

    const platformSelection = multipleDropdownComponent(
      lang.trends.filter.platforms,
      filteredPlatformOption,
      "platformList",
      true,
      175,
      false,
      true,
      "platform-dropdown"
    )

    if (selectedTab === TabOptions.STREAMS_TOP_TRACKS) {
      return (
        <div className="flex gap-4 mt-10">
          <div>{platformSelection}</div>
          <div className="mb-10">
            <div className="mb-1">
              <KSpan text={"Date"} />
            </div>
            <div>
              <div className="flex gap-2 mb-4 flex-wrap">
                <KInput
                  onChange={(text) => setDate(text)}
                  placeholder="Ex: 20250322"
                  value={date}
                  onBlur={(value) => {
                    if (value.length !== 8) {
                      toast.error("Wrong date format. Example: 20250322", errorFilledToastOptions)
                      return
                    }
                    fetchTrendTopTracks(value, true, filter).then(() =>
                      toast.success(`Success Date: ${date}`, successFilledToastOptions)
                    )
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )
    }

    if (selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY || selectedTab === TabOptions.TRACK_STREAM_SUMMARY) {
      return (
        <div className="flex justify-between  mt-8 mb-4">
          <div className="flex gap-4">
            <div>{platformSelection}</div>
            <div className="grid gap-1">
              <KSpan text={selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY ? "Min Royalty (USD)" : "Min Stream"} />
              <KInput
                value={selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY ? filter.minRoyalty : filter.minStream}
                onChange={(value) =>
                  selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY
                    ? setFilter((prevFilter) => ({ ...prevFilter, minRoyalty: value }))
                    : setFilter((prevFilter) => ({ ...prevFilter, minStream: value }))
                }
              />
            </div>
            <div className="grid gap-1">
              <KSpan text="Date" />
              <div className="flex gap-2">
                {selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY ? (
                  <KSelectRange
                    value={filter.dateValues}
                    onChange={(values) => {
                      if (!values || !values?.[0]) {
                        return
                      }
                      setFilter((prevFilter) => ({ ...prevFilter, dateValues: values }))
                    }}
                    height="36px"
                    width="auto"
                    backgroundColor="#F7F7F7"
                    hoverBackgroundColor="#F3F3F3"
                    boxShadow="none"
                  />
                ) : (
                  <KSelectRangeDate
                    value={filter.dateValues}
                    onChange={(values) => {
                      if (!values || !values?.[0]) {
                        return
                      }
                      setFilter((prevFilter) => ({ ...prevFilter, dateValues: values }))
                    }}
                    height="36px"
                    width="auto"
                    backgroundColor="#F7F7F7"
                    hoverBackgroundColor="#F3F3F3"
                    boxShadow="none"
                    align={"center"}
                  />
                )}
                <div className="flex items-center">
                  {filter.dateValues?.[0] && (
                    <KSpan text={`${toLocaleDate(filter.dateValues[0])} - ${toLocaleDate(filter.dateValues[1])}`} />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="grid" style={{ alignContent: "end" }}>
            <KButton
              width="120px"
              background="black"
              textColor="white"
              text="Apply Filters"
              onClick={() => applySummaryFilter()}
            />
          </div>
        </div>
      )
    }

    return <div className="mb-4 flex"></div>
  }

  const renderTabs = () => {
    return (
      <div className="w-full flex flex-row items-center justify-between gap-3 flex-wrap-reverse">
        <div className="flex gap-4">
          {checkAdminPermission(ADMIN_PERMISSIONS.ADMIN_INSIGHTS_TOP_100, user?.adminPermissions) && (
            <div
              className="cursor-pointer"
              onClick={() => {
                resetFilters()
                setSelectedTab(TabOptions.STREAMS_TOP_TRACKS)
              }}
              style={{ borderBottom: selectedTab === TabOptions.STREAMS_TOP_TRACKS ? "1px solid #666666" : "" }}
            >
              <KSpan
                text={"Top 100"}
                fontWeight={selectedTab === TabOptions.STREAMS_TOP_TRACKS ? 500 : 400}
                color={selectedTab === TabOptions.STREAMS_TOP_TRACKS ? "#111111" : ""}
              />
            </div>
          )}
          {checkAdminPermission(ADMIN_PERMISSIONS.ADMIN_INSIGHTS_STREAMS_L3M, user?.adminPermissions) && (
            <div
              className="cursor-pointer"
              onClick={() => {
                resetFilters()
                setSelectedTab(TabOptions.STREAMS_TRACK_MONTHS)
              }}
              style={{ borderBottom: selectedTab === TabOptions.STREAMS_TRACK_MONTHS ? "1px solid #666666" : "" }}
            >
              <KSpan
                text={"Streams Last 12 Months"}
                fontWeight={selectedTab === TabOptions.STREAMS_TRACK_MONTHS ? 500 : 400}
                color={selectedTab === TabOptions.STREAMS_TRACK_MONTHS ? "#111111" : ""}
              />
            </div>
          )}
          {checkAdminPermission(ADMIN_PERMISSIONS.ADMIN_INSIGHTS_ROYALTY_L3M, user?.adminPermissions) && (
            <div
              className="cursor-pointer"
              onClick={() => {
                resetFilters()
                setSelectedTab(TabOptions.ROYALTY_THREE_MONTHS)
              }}
              style={{ borderBottom: selectedTab === TabOptions.ROYALTY_THREE_MONTHS ? "1px solid #666666" : "" }}
            >
              <KSpan
                text={"Royalty Last 12 Months"}
                fontWeight={selectedTab === TabOptions.ROYALTY_THREE_MONTHS ? 500 : 400}
                color={selectedTab === TabOptions.ROYALTY_THREE_MONTHS ? "#111111" : ""}
              />
            </div>
          )}
          {checkAdminPermission(ADMIN_PERMISSIONS.ADMIN_INSIGHTS_ROYALTY_SUMMARY, user?.adminPermissions) && (
            <div
              className="cursor-pointer"
              onClick={() => {
                resetFilters()
                setSelectedTab(TabOptions.TRACK_ROYALTY_SUMMARY)
              }}
              style={{ borderBottom: selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY ? "1px solid #666666" : "" }}
            >
              <KSpan
                text={"Track Royalty Summary"}
                fontWeight={selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY ? 500 : 400}
                color={selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY ? "#111111" : ""}
              />
            </div>
          )}
          {checkAdminPermission(ADMIN_PERMISSIONS.ADMIN_INSIGHTS_STREAM_SUMMARY, user?.adminPermissions) && (
            <div
              className="cursor-pointer"
              onClick={() => {
                resetFilters()
                setSelectedTab(TabOptions.TRACK_STREAM_SUMMARY)
              }}
              style={{ borderBottom: selectedTab === TabOptions.TRACK_STREAM_SUMMARY ? "1px solid #666666" : "" }}
            >
              <KSpan
                text={"Track Stream Summary"}
                fontWeight={selectedTab === TabOptions.STREAMS_TRACK_MONTHS ? 500 : 400}
                color={selectedTab === TabOptions.STREAMS_TRACK_MONTHS ? "#111111" : ""}
              />
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex bg-[#fff] h-screen">
      <div className="grow flex justify-center overflow-x-auto p-3 h-screen min-h-0">
        <div
          className="w-full flex flex-col items-center h-full"
          style={{
            paddingRight: 40,
            paddingLeft: 20
          }}
        >
          <div className="pt-5 w-full flex-1 min-h-0 flex flex-col">
            {renderTabs()}
            {renderFilters()}
            {loading && <Loading />}
            {!loading && selectedTab === TabOptions.STREAMS_TOP_TRACKS && (
              <StreamsTopTracks trendTopTracks={trendTopTracks} />
            )}
            {!loading && selectedTab === TabOptions.STREAMS_TRACK_MONTHS && (
              <StreamsTrackMonths
                artistTrendEconomyData={artistTrendEconomyData}
                offset={offset}
                setOffset={setOffset}
                limit={limit}
                selectedRoyaltyType={selectedRoyaltyType}
                setSelectedRoyaltyType={setSelectedRoyaltyType}
                selectedResults={selectedResults}
                setSelectedResults={setSelectedResults}
                hasNext={hasNext}
              />
            )}
            {!loading && selectedTab === TabOptions.ROYALTY_THREE_MONTHS && (
              <RoyaltyThreeMonths
                artistTrendEconomyData={artistTrendEconomyData}
                offset={offset}
                setOffset={setOffset}
                limit={limit}
                selectedRoyaltyType={selectedRoyaltyType}
                setSelectedRoyaltyType={setSelectedRoyaltyType}
                selectedResults={selectedResults}
                setSelectedResults={setSelectedResults}
                hasNext={hasNext}
              />
            )}
            {!loading && selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY && (
              <SummaryTrackRoyalties
                summaryTrackRoyalties={summaryTrackRoyalties}
                handleSort={handleSort}
                sort={sort}
              />
            )}
            {!loading && selectedTab === TabOptions.TRACK_STREAM_SUMMARY && (
              <SummaryTrackStreams summaryTrackStreams={summaryTrackStreams} handleSort={handleSort} sort={sort} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    options: state.trends.options,
    trendTopTracks: state.trends.trendTopTracks,
    streamsTrackMonths: state.trends.streamsTrackMonths,
    artistTrendEconomyData: state.trends.artistTrendEconomyData,
    summaryTrackRoyalties: state.trends.summaryTrackRoyalties,
    summaryTrackStreams: state.trends.summaryTrackStreams,
    user: state.auth.user
  }
}

export default connect(mapStateToProps, {
  fetchTrendTopTracks,
  fetchStreamsTrackMonths,
  fetchArtistTrendEconomyData,
  fetchSummaryTrackRoyalties,
  fetchSummaryTrackStreams
})(AdminTrends)
